/*************************************************************
 * useAutoRefresh.js - Auto-refresh hook for analytics components
 *************************************************************/

import { useEffect, useRef } from 'react';
import { useAppContext } from '../context/AppContext';

/**
 * Hook that automatically refreshes data based on user preferences
 * @param {Function} refreshFunction - Function to call for refreshing data
 * @param {Array} dependencies - Additional dependencies to watch for changes
 * @param {Object} options - Additional options
 */
export function useAutoRefresh(refreshFunction, dependencies = [], options = {}) {
    const { state } = useAppContext();
    const intervalRef = useRef();
    const lastRefreshRef = useRef(0);
    
    const {
        minInterval = 10, // Minimum seconds between refreshes
        maxInterval = 300, // Maximum seconds between refreshes
        pauseOnHidden = true, // Pause when page/tab is hidden
        logRefresh = true // Log refresh activity
    } = options;
    
    useEffect(() => {
        // Clear existing interval
        if (intervalRef.current) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
        }
        
        // Only set up auto-refresh if enabled in preferences
        if (!state.preferences.autoRefresh) {
            if (logRefresh) {
                console.log('[AutoRefresh] Auto-refresh disabled by user preference');
            }
            return;
        }
        
        // Validate refresh interval
        let intervalSeconds = state.preferences.refreshInterval || 30;
        intervalSeconds = Math.max(minInterval, Math.min(maxInterval, intervalSeconds));
        const intervalMs = intervalSeconds * 1000;
        
        // Create refresh wrapper that respects rate limiting
        const wrappedRefresh = () => {
            const now = Date.now();
            const timeSinceLastRefresh = now - lastRefreshRef.current;
            
            // Prevent too frequent refreshes
            if (timeSinceLastRefresh < minInterval * 1000) {
                if (logRefresh) {
                    console.log('[AutoRefresh] Skipping refresh - too soon after last refresh');
                }
                return;
            }
            
            // Check if page is hidden (optional optimization)
            if (pauseOnHidden && document.hidden) {
                if (logRefresh) {
                    console.log('[AutoRefresh] Skipping refresh - page is hidden');
                }
                return;
            }
            
            if (logRefresh) {
                console.log('[AutoRefresh] Executing auto-refresh...');
            }
            
            try {
                refreshFunction();
                lastRefreshRef.current = now;
            } catch (error) {
                console.error('[AutoRefresh] Error during auto-refresh:', error);
            }
        };
        
        // Set up the interval
        intervalRef.current = setInterval(wrappedRefresh, intervalMs);
        
        if (logRefresh) {
            console.log(`[AutoRefresh] Auto-refresh enabled: every ${intervalSeconds} seconds`);
        }
        
        // Cleanup function
        return () => {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
                intervalRef.current = null;
                if (logRefresh) {
                    console.log('[AutoRefresh] Auto-refresh stopped');
                }
            }
        };
    }, [
        state.preferences.autoRefresh, 
        state.preferences.refreshInterval, 
        refreshFunction,
        ...dependencies
    ]);
    
    // Pause/resume when page visibility changes
    useEffect(() => {
        if (!pauseOnHidden) return;
        
        const handleVisibilityChange = () => {
            if (document.hidden) {
                // Page is hidden - interval will naturally skip refreshes
                if (logRefresh) {
                    console.log('[AutoRefresh] Page hidden - refreshes will be paused');
                }
            } else {
                // Page is visible again
                if (logRefresh) {
                    console.log('[AutoRefresh] Page visible - refreshes resumed');
                }
            }
        };
        
        document.addEventListener('visibilitychange', handleVisibilityChange);
        return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
    }, [pauseOnHidden, logRefresh]);
    
    // Return utility functions
    return {
        isActive: state.preferences.autoRefresh && !!intervalRef.current,
        interval: state.preferences.refreshInterval || 30,
        forceRefresh: () => {
            if (refreshFunction) {
                refreshFunction();
                lastRefreshRef.current = Date.now();
            }
        }
    };
}